import { globals } from 'config/globals';
import { particles } from 'app/contracts/Particles';
import Config from 'config/settings';
import { ParticleLayoutType } from 'app/enum/Particles';
import { HomeField } from 'app/views/home/webgl/particles/fields/HomeField';
import HomeFieldConfig from 'app/views/home/webgl/particles/config/HomeFieldConfig';
import { HomeParticle } from 'app/views/home/webgl/particles/fields/HomeParticle';
import DepartmentColours from 'app/views/home/webgl/particles/departments/DepartmentColours';
import ParticleGridTargets from 'app/views/home/webgl/particles/targets/ParticleGridTargets';
import SingleLine from 'app/views/home/webgl/particles/fields/gridItems/SingleLine';
import ParticlePool from 'app/views/home/webgl/particles/fields/ParticlePool';
import ParallaxItemTargets from 'app/views/home/targets/ParallaxItemTargets';
import ParticleGridPatterns from 'app/views/home/webgl/particles/targets/ParticleGridPatterns';
import HomeColours from 'app/views/home/config/HomeColours';
import ComboCounter from 'utils/ComboCounter';
import { Simple } from 'utils/analytics';
import BaseDepartmentScene from 'app/views/home/webgl/particles/departments/BaseDepartmentScene';
import { device } from 'utils/device';

export default class SystemsDepartment extends BaseDepartmentScene {
	private _active: boolean = false;
	private _spacesStageIndex = 0;
	private _spacesStageCounter = 0;
	private _systemsParticles: Array<HomeParticle>;
	private _transitionParticles: Array<HomeParticle>;
	private _systemsParticleContainer: PIXI.Sprite;

	private _lines: Array<SingleLine> = [];

	private _resizeTimeout: number;

	private _spacesBoxStages = [
		{ type: 'SingleLine', frames: 10000 }
	];

	private _lineModeIndex = 0;
	private _lineModeCounter = 0;
	private _lineModeChangeAtCount = 0;

	private _colourModeCount = 0;
	private _colourModeChangeAtCount = 3000 / 16;

	private _lineModeSettings = [
		{ rightAngle: false, singleLineMode: false, timeMS: 15000 },
		{ rightAngle: false, singleLineMode: true, timeMS: 5000 },
		{ rightAngle: true, singleLineMode: false, timeMS: 15000 },
		{ rightAngle: true, singleLineMode: true, timeMS: 10000 },
		{ rightAngle: false, singleLineMode: true, timeMS: 10000 },
		{ rightAngle: false, singleLineMode: false, timeMS: 15000 },
		{ rightAngle: true, singleLineMode: true, timeMS: 10000 },
		{ rightAngle: true, singleLineMode: false, timeMS: 10000 }
	];

	private _counters: ComboCounter;
	private _firstColour;
	private _firstColourSet = false;

	constructor(
		private _homeField: HomeField,
		private _sceneProps: particles.SceneProps,
		private _colourProcessor: DepartmentColours
	) {
		super();
		this._counters = new ComboCounter();

		this._systemsParticles = HomeFieldConfig.getParticlePool(
			this._homeField.particles as Array<HomeParticle>,
			'systems'
		);
		ParticlePool.setPool('systems', this._systemsParticles);

		this._transitionParticles = HomeFieldConfig.getParticlePool(
			this._homeField.particles as Array<HomeParticle>,
			'transitions'
		);
		this._systemsParticles.concat(this._transitionParticles);
		this._systemsParticleContainer = this._homeField.getSubContainer('systems');


		this._systemsParticleContainer.visible = false;

		this._firstColour = HomeColours.getRandomColour(HomeColours.greenBlues);
	}

	public get isActive(): boolean {
		return this._active;
	}

	/**
	 * When skipping upodate then need still animate the grid across:
	 */
	public updateGrid() {
		this._generateBrokenGrid();
	}

	public tick() {
		this._positionParticleContainer();
		this._generateBrokenGrid();

		if (!this._firstColourSet) {
			let isSet = this._colourProcessor.changeSystemsColour([globals.accentColour], true);
			this._firstColourSet = isSet;
		}

		if (!this._active) return;

		this._counters.tick();
		this._tickLineMode();
		this._tickMarkerColour();

		for (let i = 0; i < this._lines.length; i++) {
			this._lines[i].tick();
		}

		this._counter();
	}

	public resize() {
		this.stopAsActiveScene();

		if (this._resizeTimeout) {
			clearTimeout(this._resizeTimeout);
		}

		this._resizeTimeout = setTimeout(() => {
			this.startAsActiveScene();
			this._resizeTimeout = null;
		}, 500);
	}

	public showFromWork() {
		this.stopAsActiveScene();
		this.startAsActiveScene();
	}

	private _generateBrokenGrid() {
		let gridSprite: PIXI.Sprite = ParallaxItemTargets.get('systems-grid');

		//Undefined when the systems-grid item is missing (webglTypes-filtered
		//— see TimelineItemFactory) or hasn't sprite-bound yet — skip the pass:
		if (!gridSprite) {
			return;
		}

		let w = gridSprite.width;
		let h = gridSprite.height;
		ParticleGridTargets.tick(
			gridSprite.x,
			device.innerHeight / 2,
			w,
			h,
			this._sceneProps.progress.ratio
		);
	}

	/** User is far enough right to be on spaces scene */
	public startAsActiveScene(reposition: boolean = true) {
		globals.accentColour = this._firstColour;
		this._colourProcessor.changeSystemsColour([globals.accentColour], true);

		let gridSprite: PIXI.Sprite = ParallaxItemTargets.get('systems-grid');

		//Undefined when the systems-grid item is missing (webglTypes-filtered
		//— see TimelineItemFactory) — skip the grid-target pass, keep the rest:
		if (gridSprite) {
			let w = gridSprite.width * gridSprite.scale.x;
			let h = gridSprite.height * gridSprite.scale.y;

			ParticleGridTargets.tick(gridSprite.x, device.innerWidth / 2, w, h, this._sceneProps.progress.ratio);
		}

		this._spacesStageIndex = 0;
		this._spacesStageCounter = 0;


		this._counters.clear();

		this._counters.add(() => {
			globals.skipNormalParticleUpdates = true;
			this._systemsParticles.forEach((particle) => {
				particle.systems.setSystemsStart();
			});
		}, 50);

		this._counters.add(() => {
			globals.skipNormalParticleUpdates = true;
			this._nextSystemsStage();
		}, 100);

		this._counters.add(() => {
			this._systemsParticleContainer.visible = true;
		}, 120);

		this._active = true;
	}

	public stopAsActiveScene() {
		console.log('Systems stopAsActiveScene');

		this._counters.clear();

		this._firstColourSet = false;
		this._active = false;
		this._systemsParticleContainer.visible = false;

		this._clearAllLines();

		this._systemsParticles.forEach((particle) => {
			particle.position.set({ x: 2, y: 2 }, true);
			particle.deactivateAllBehaviours(false);
			particle.setVisible(false);
			particle.animatedProps.targetAlpha = particle.animatedProps.alpha = 0;
			particle.active = false;
			particle.pooled = false;
		});
	}

	public toWork() {

		this._reduceProcessingForWorkTransition();

		gsap.TweenMax.to(this._systemsParticleContainer, Config.toWorkTime, {
			pixi: { y: -1 * device.innerHeight },
			ease: Config.toWorkEase,
			onComplete: () => {
				this._disableForWork();
			}
		});
	}

	public fromWorkToHome() {
		this._enableForWork();
		this.startAsActiveScene(false);

		gsap.TweenMax.to(this._systemsParticleContainer, Config.toWorkTime, {
			pixi: { y: 0 },
			ease: Config.toWorkEase,
			onComplete: () => {}
		});
	}

	//PRIVATE
	//--------------------------------------------------------------------------------------
	//--------------------------------------------------------------------------------------
	//--------------------------------------------------------------------------------------
	//--------------------------------------------------------------------------------------
	//--------------------------------------------------------------------------------------

	private _positionParticleContainer() {
		let x =
			this._sceneProps.progress.containerX +
			this._sceneProps.zonePositions.systemsParticles.cx * this._sceneProps.sizes.container.w;

		this._systemsParticleContainer.x = x;
		this._sceneProps.mouse.particleContainersX.systems = x;
	}

	private _reduceProcessingForWorkTransition() {
		this._active = false;
		this._systemsParticles.forEach((p: HomeParticle) => {
			p.repell.active = false;
		});
	}

	private _disableForWork(): void {
		this._systemsParticleContainer.visible = false;

		this._systemsParticles.forEach((p: HomeParticle) => {
			p.deactivateAllBehaviours(false);
			p.active = false;
		});
	}

	private _enableForWork(): void {
		this._systemsParticleContainer.visible = true;
		this._systemsParticles.forEach((p: HomeParticle) => {
			p.active = true;
		});
	}

	private _counter(): void {
		this._spacesStageCounter++;
		if (this._spacesStageCounter >= this._spacesBoxStages[this._spacesStageIndex].frames) {
			this._spacesStageCounter = 0;

			//Deal with the index change:
			this._spacesStageIndex++;
			if (this._spacesStageIndex >= this._spacesBoxStages.length) {
				this._spacesStageIndex = 0;
			}

			this._nextSystemsStage();
		}
	}

	private _nextSystemsStage(): void {
		let type: string = this._spacesBoxStages[this._spacesStageIndex].type;

		if (type === 'Rain') this._setSpacesRain();
		else if (type === 'BasicGrid') this._spacesBasicGrid();
		else if (type === 'SingleLine') this._singleLine();


	}

	private _setSpacesRain(): void {
		this._systemsParticles.forEach((particle) => {
			particle.systems.rain();
		});
	}

	private _spacesBasicGrid(): void {
		let box = this._sceneProps.zonePositions.systemsPArea;

		this._homeField.reposition(
			ParticleLayoutType.GRID,
			{ x: -0.08, y: box.y },
			{ w: box.w, h: 20 },
			null,
			this._systemsParticles,
			1000,
			true
		);

		this._systemsParticles.forEach((particle) => particle.systems.basicGrid());
	}

	private _clearAllLines() {
		for (let i = 0; i < this._lines.length; i++) {
			this._lines[i].clear();
		}

		this._lines = [];
	}

	private _singleLine(): void {
		//IDEAS:
		// Ping-pong particles
		// Mini grid, i.e 1-4 in a group to make grids
		// Colour shifts
		//Curved movement (created by difference in x/y speeds).
		//Bounce off edge and exit grid etc with physics

		globals.singleLineMode = false;
		globals.rightAngleMode = false;

		let totalLines = device.innerWidth < 600 ? 8 : 10;
		let delay = 0;

		for (let i = 0; i < totalLines; i++) {
			if (this._lines.length > 10) {
				return;
			}

			let spawn = ParticleGridPatterns.getSpawnTarget();

			let end = ParticleGridPatterns.get45DegreeTarget(spawn.col, spawn.row, 8);

			if (!end) {
				end = ParticleGridPatterns.get45DegreeTarget(spawn.col, spawn.row, 8);
			}

			if (!end) {
				end = ParticleGridPatterns.get45DegreeTarget(spawn.col, spawn.row, 8);
			}

			if (!end) {
				end = ParticleGridPatterns.get45DegreeTarget(spawn.col, spawn.row, 8);
			}

			if (!end) {
				continue;
			}

			let lineConfig = {
				start: `grid_${spawn.col}_${spawn.row}`,
				end: `grid_${end.col}_${end.row}`
			};


			let line = new SingleLine(lineConfig.start, lineConfig.end, 1, 1, 40000, 'systems', delay);

			line.endTargetGridPoint = end;

			this._lines.push(line);


			delay += 100;
		}

		globals.fastModeScale = 0.8;

		globals.fastModeScale = 0.6;
		let tweenOb = { value: globals.fastModeScale };
		gsap.TweenMax.to(tweenOb, 5, {
			value: 0.95,
			ease: Config.linearEase,
			onUpdate: () => {
				globals.fastModeScale = tweenOb.value;
			}
		});

		this._lineModeIndex = 0;
		this._lineModeCounter = 0;
		this._setLineMode(true);
		this._lineModeIndex = 1;


		globals.accentColour = this._firstColour;
		this._colourProcessor.changeSystemsColour([globals.accentColour], true);
	}

	private _totalPasses = 0;
	private _tickLineMode() {
		this._lineModeCounter += (window as any).deltaTime;

		if (this._lineModeCounter >= this._lineModeChangeAtCount) {
			this._lineModeIndex++;
			if (this._lineModeIndex >= this._lineModeSettings.length) {
				Simple.event('home_systems_full_phase_complete', { total_passes: this._totalPasses });
				this._lineModeIndex = 0;
			}

			Simple.event('home_systems_next_phase', { index: this._lineModeIndex });

			this._lineModeCounter = 0;

			if (this._lineModeIndex) {
				this._setLineMode();
			}
		}
	}

	private _setLineMode(firstPass: boolean = false) {
		globals.singleLineMode = this._lineModeSettings[this._lineModeIndex].singleLineMode;
		globals.rightAngleMode = this._lineModeSettings[this._lineModeIndex].rightAngle;
		this._lineModeChangeAtCount = this._lineModeSettings[this._lineModeIndex].timeMS
			? this._lineModeSettings[this._lineModeIndex].timeMS / 16
			: 10000 / 16;

		this._lineModeChangeAtCount *= 0.8;

	}

	private _tickMarkerColour() {
		this._colourModeCount += (window as any).deltaTime;

		if (this._colourModeCount >= this._colourModeChangeAtCount) {

			globals.accentColour = HomeColours.getRandomColour(HomeColours.greenBlues);

			this._colourProcessor.changeSystemsColour([globals.accentColour]);

			this._colourModeCount = 0;
		}
	}
}
